import React from "react";

import { DesignLibraryColorPresets } from "@editor/components/shops-ui/designLibrary/DesignLibraryColorPresets";
import { DesignLibraryColors } from "@editor/components/shops-ui/designLibrary/DesignLibraryColors";
import { DesignLibraryCustomFonts } from "@editor/components/shops-ui/designLibrary/DesignLibraryCustomFonts";
import { DesignLibraryFontPresets } from "@editor/components/shops-ui/designLibrary/DesignLibraryFontPresets";
import { DesignLibrarySummary } from "@editor/components/shops-ui/designLibrary/DesignLibrarySummary";
import { DesignLibraryTypography } from "@editor/components/shops-ui/designLibrary/DesignLibraryTypography";
import { DiscardDesignLibraryChangesModal } from "@editor/components/shops-ui/designLibrary/DiscardDesignLibraryChangesModal";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useDesignLibrary } from "@editor/hooks/useDesignLibrary";

import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { X } from "lucide-react";

import { useEditorStoreActions } from "../store/editorStore";

export default function DesignLibraryPanel() {
  const { setPanelView } = useEditorStoreActions();
  const projectId = useCurrentProjectId();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = React.useState(false);

  const {
    currentView,
    setCurrentView,
    currentStyleLibrary,
    hasUnsavedChanges,
    handleStyleLibraryChange,
    handleDiscard,
    handleSave,
    handleCustomFontsClick,
    handleCustomFontsBack,
  } = useDesignLibrary(projectId);

  const handleConfirmClose = async () => {
    setIsDiscardModalOpen(false);
    await handleDiscard();
    setPanelView("chat");
  };

  const handleCancelClose = () => {
    setIsDiscardModalOpen(false);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      setPanelView("chat");
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="flex h-full min-h-0 w-full flex-col border border-border rounded-xl shadow-button-selectable">
        {currentView === "summary" && (
          <div className="flex items-center justify-between p-3 border-b-0.5 border-border">
            <h2 className="header-h3">Design Library</h2>
            <IconButton
              variant="tertiary"
              size="base"
              onClick={handleClose}
              icon={<X size={16} />}
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto [&>*]:border-0 [&>*]:shadow-none [&>*]:bg-transparent [&>*]:p-0">
          {currentView === "summary" && (
            <DesignLibrarySummary
              styleLibrary={currentStyleLibrary}
              onColorsClick={() => setCurrentView("colors")}
              onTypographyClick={() => setCurrentView("typography")}
              onCustomFontsClick={handleCustomFontsClick}
              onStyleLibraryChange={(styleLibrary) =>
                void handleStyleLibraryChange(styleLibrary)
              }
            />
          )}
          {currentView === "colors" && (
            <DesignLibraryColors
              styleLibrary={currentStyleLibrary}
              onBack={() => setCurrentView("summary")}
              onPresetsClick={() => setCurrentView("presets")}
              onStyleLibraryChange={(styleLibrary) =>
                void handleStyleLibraryChange(styleLibrary)
              }
            />
          )}
          {currentView === "presets" && (
            <DesignLibraryColorPresets
              styleLibrary={currentStyleLibrary}
              onBack={() => setCurrentView("colors")}
              onStyleLibraryChange={(styleLibrary) =>
                void handleStyleLibraryChange(styleLibrary)
              }
            />
          )}
          {currentView === "typography" && (
            <DesignLibraryTypography
              styleLibrary={currentStyleLibrary}
              onBack={() => setCurrentView("summary")}
              onCustomFontsClick={handleCustomFontsClick}
              onFontPresetsClick={() => setCurrentView("fontPresets")}
              onStyleLibraryChange={(styleLibrary) =>
                void handleStyleLibraryChange(styleLibrary)
              }
            />
          )}
          {currentView === "fontPresets" && (
            <DesignLibraryFontPresets
              styleLibrary={currentStyleLibrary}
              onBack={() => setCurrentView("typography")}
              onStyleLibraryChange={(styleLibrary) =>
                void handleStyleLibraryChange(styleLibrary)
              }
            />
          )}
          {currentView === "customFonts" && (
            <DesignLibraryCustomFonts onBack={handleCustomFontsBack} />
          )}
        </div>
        <div className="flex justify-end gap-3 p-4">
          <Button
            variant="secondary"
            size="base"
            onClick={() => void handleDiscard()}
            disabled={!hasUnsavedChanges}
          >
            Discard
          </Button>
          <Button
            variant="primary"
            size="base"
            onClick={() => {
              void (async () => {
                await handleSave();
                successToast(
                  "Your style library has successfully been updated.",
                );
              })();
            }}
            disabled={!hasUnsavedChanges}
          >
            Save Changes
          </Button>
        </div>
      </div>

      <DiscardDesignLibraryChangesModal
        isOpen={isDiscardModalOpen}
        onOpenChange={setIsDiscardModalOpen}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </div>
  );
}
